import React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { Download, RefreshCw } from "lucide-react";

import PageHeader from "../../components/PageHeader";
import { COLORS } from "../../constants";
import {
  BackupDiskInfo,
  servidorOpsService,
} from "../../services/servidorOpsService";

function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("pt-BR", {
      timeZone: "America/Sao_Paulo",
    });
  } catch {
    return iso;
  }
}

function formatBytes(bytes: number | null | undefined): string {
  if (bytes == null) return "—";
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let value = bytes / 1024;
  let i = 0;
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024;
    i += 1;
  }
  return `${value.toFixed(value >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
}

function diskLine(disk: BackupDiskInfo | null | undefined): string | null {
  if (!disk || disk.usedBytes == null || disk.totalBytes == null) return null;
  const pct =
    disk.usedRatio != null
      ? ` (${(disk.usedRatio * 100).toFixed(1)}%)`
      : "";
  return `${formatBytes(disk.usedBytes)} / ${formatBytes(disk.totalBytes)}${pct}`;
}

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <p>
    <span className="font-medium">{label}:</span> {children}
  </p>
);

const BackupsServidor: React.FC = () => {
  const queryClient = useQueryClient();
  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ["desenvolvedor", "backups", "status"],
    queryFn: () => servidorOpsService.getBackupStatus(),
    refetchInterval: 60_000,
  });

  const validateMut = useMutation({
    mutationFn: () => servidorOpsService.validateBackup(),
    onSuccess: (r) => {
      if (r.ok) toast.success(r.detail);
      else toast.warn(r.detail);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const downloadMut = useMutation({
    mutationFn: () => servidorOpsService.downloadPostgresBackup(),
    onSuccess: () => toast.success("Download iniciado"),
    onError: (e: Error) => toast.error(e.message),
  });

  const pg = data?.postgres;
  const sourceHost = pg?.sourceHost;
  const engine = pg?.engine ?? (sourceHost ? "PostgreSQL 18" : null);
  const dbDisk = diskLine(data?.storage?.dbDisk);
  const appDisk = diskLine(data?.storage?.appDisk);

  return (
    <div className="p-4 md:p-6 max-w-3xl mx-auto space-y-6">
      <PageHeader
        title="Backups do servidor"
        description="Dump do Postgres 18 dedicado (04:00). Cópia para este app às 04:15. Sempre sobrescreve o último arquivo."
      />

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-white"
          style={{ background: COLORS.PRIMARY }}
          onClick={() => refetch()}
          disabled={isFetching}
        >
          <RefreshCw size={16} className={isFetching ? "animate-spin" : ""} />
          Atualizar
        </button>
        <button
          type="button"
          className="inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm"
          disabled={!pg?.available || downloadMut.isPending}
          onClick={() => downloadMut.mutate()}
        >
          <Download size={16} />
          Baixar dump de produção
        </button>
        <button
          type="button"
          className="inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm"
          disabled={!pg?.available || validateMut.isPending}
          onClick={() => validateMut.mutate()}
        >
          Validar dump
        </button>
      </div>

      {isLoading ? (
        <p className="text-sm text-neutral-500">Carregando…</p>
      ) : (
        <div className="rounded-xl border border-neutral-200 bg-white p-4 space-y-3 text-sm">
          <Field label="Origem">
            {engine || sourceHost
              ? `${engine ?? "PostgreSQL"}${sourceHost ? ` em ${sourceHost}` : " (servidor dedicado)"}`
              : "PostgreSQL 18 no servidor dedicado"}
          </Field>
          <Field label="Arquivo">
            {pg?.available ? pg.filename : "ainda não gerado"}
          </Field>
          <Field label="Tamanho">{pg?.sizeHuman ?? "—"}</Field>
          <Field label="Gerado no banco">
            {formatDate(pg?.dumpedAt)}
            {pg?.ageHours != null && pg?.dumpedAt
              ? ` (${pg.ageHours}h atrás)`
              : ""}
          </Field>
          <Field label="Copiado para este app">
            {formatDate(pg?.syncedAt ?? pg?.modifiedAt ?? null)}
          </Field>
          {dbDisk ? <Field label="Disco do banco">{dbDisk}</Field> : null}
          {appDisk ? <Field label="Disco deste app">{appDisk}</Field> : null}
          <p className="text-neutral-600">{data?.notes.schedule}</p>
          <p className="text-neutral-600">{data?.notes.retention}</p>
          <p className="text-neutral-600">
            {data?.notes.localSync ?? (
              <>
                Importar no Postgres local:{" "}
                <code className="text-xs bg-neutral-100 px-1 rounded">
                  npm run sync:local
                </code>{" "}
                na raiz do Backend.
              </>
            )}
          </p>
        </div>
      )}

      <button
        type="button"
        className="text-xs text-neutral-400 underline"
        onClick={() =>
          queryClient.invalidateQueries({
            queryKey: ["desenvolvedor", "backups"],
          })
        }
      >
        limpar cache
      </button>
    </div>
  );
};

export default BackupsServidor;
